import Fuse from 'fuse.js';

import type { ReportCourse } from '@/hooks/queries/useReportCourses';
import { removeCommentTags } from '@/lib/utils';
import { courseGrades } from '@/Shared/Data/StaticData';

const gradeOrder = new Map(courseGrades.map((g, index) => [g.value, index] as const));

export type ReportFilters = {
	disciplineId: string;
	divisionId: string;
	grade: string;
	includeArchived: boolean;
};

export const emptyReportFilters: ReportFilters = {
	disciplineId: '',
	divisionId: '',
	grade: '',
	includeArchived: false,
};

export function compareGrades(a: string | null | undefined, b: string | null | undefined): number {
	const aKey = a ?? '';
	const bKey = b ?? '';
	const aIndex = gradeOrder.has(aKey) ? gradeOrder.get(aKey)! : Number.MAX_SAFE_INTEGER;
	const bIndex = gradeOrder.has(bKey) ? gradeOrder.get(bKey)! : Number.MAX_SAFE_INTEGER;
	if (aIndex !== bIndex) return aIndex - bIndex;
	return aKey.localeCompare(bKey);
}

/** Strip TipTap/HTML markup for report tables and CSV (preserves display casing). */
export function htmlToPlainText(raw: string | null | undefined): string {
	if (!raw) return '';
	const withoutComments = removeCommentTags(raw);
	const doc = new DOMParser().parseFromString(withoutComments, 'text/html');
	return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function asArray<T>(value: T | T[] | null | undefined): T[] {
	if (!value) return [];
	return Array.isArray(value) ? value : [value];
}

export function getCourseDiscipline(course: ReportCourse): { id: string; name: string } | null {
	const rows = asArray(course.courses_disciplines as any);
	const discipline = rows[0]?.discipline;
	if (!discipline?.id) return null;
	return { id: discipline.id, name: discipline.name ?? '' };
}

export function getCourseDivision(course: ReportCourse): { id: string; name: string } | null {
	const rows = asArray(course.courses_divisions as any);
	const division = rows[0]?.division;
	if (!division?.id) return null;
	return { id: division.id, name: division.name ?? '' };
}

export function getCourseOwners(course: ReportCourse): string {
	const users = course.courses_users ?? [];
	return users
		.map((cu) => {
			const first = cu.user?.user_first?.trim() ?? '';
			const last = cu.user?.user_last?.trim() ?? '';
			return `${first} ${last}`.trim();
		})
		.filter(Boolean)
		.join(', ');
}

export function getCourseStatus(course: ReportCourse): string {
	if (course.is_archived) return 'Archived';
	if (course.is_saved_draft) return 'Draft';
	return 'Active';
}

export function filterReportCourses(
	courses: ReportCourse[],
	filters: ReportFilters
): ReportCourse[] {
	return courses.filter((course) => {
		if (!filters.includeArchived && course.is_archived) return false;

		if (filters.disciplineId) {
			const discipline = getCourseDiscipline(course);
			if (discipline?.id !== filters.disciplineId) return false;
		}

		if (filters.divisionId) {
			const division = getCourseDivision(course);
			if (division?.id !== filters.divisionId) return false;
		}

		if (filters.grade) {
			if ((course.grade ?? '') !== filters.grade) return false;
		}

		return true;
	});
}

export function escapeCsvValue(value: string | number | boolean | null | undefined): string {
	const raw = value == null ? '' : String(value);
	if (/[",\n\r]/.test(raw)) {
		return `"${raw.replace(/"/g, '""')}"`;
	}
	return raw;
}

export function downloadCsv(
	filename: string,
	headers: string[],
	rows: Array<Array<string | number | boolean | null | undefined>>
): void {
	const lines = [
		headers.map(escapeCsvValue).join(','),
		...rows.map((row) => row.map(escapeCsvValue).join(',')),
	];
	const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

export function reportCsvFilename(reportSlug: string, schoolId: string): string {
	const date = new Date().toISOString().slice(0, 10);
	const safeSchool = schoolId || 'school';
	return `elevate-${reportSlug}-${safeSchool}-${date}.csv`;
}

export type MaterialsRow = {
	courseId: string;
	resourceId: string;
	courseResourcesId: string;
	grade: string;
	discipline: string;
	course: string;
	resourceTitle: string;
	resourceType: string;
	author: string;
	isbnOrDetail: string;
	/** Visible columns only (never ISBN/detail). */
	searchText: string;
};

export function buildMaterialsRows(courses: ReportCourse[]): MaterialsRow[] {
	const rows: MaterialsRow[] = [];

	for (const course of courses) {
		const discipline = getCourseDiscipline(course)?.name ?? '';
		const grade = course.grade ?? '';
		const courseName = course.course_name ?? '';
		const resources = course.courses_resources ?? [];

		for (const cr of resources) {
			const resource = Array.isArray(cr.resource) ? cr.resource[0] : cr.resource;
			if (!resource) continue;
			const resourceTitle = htmlToPlainText(resource.resource_title);
			const resourceType = htmlToPlainText(resource.resource_type);
			const author = htmlToPlainText(resource.resource_author);
			// Prefer ISBN for display; fall back to plain detail. Never included in searchText.
			const isbnOrDetail = resource.resource_isbn?.trim()
				? resource.resource_isbn.trim()
				: htmlToPlainText(resource.resource_detail);

			rows.push({
				courseId: course.course_id,
				resourceId: resource.resource_id,
				courseResourcesId: cr.course_resources_id,
				grade,
				discipline,
				course: courseName,
				resourceTitle,
				resourceType,
				author,
				isbnOrDetail,
				searchText: [grade, discipline, courseName, resourceTitle, resourceType, author].join(' '),
			});
		}
	}

	return rows.sort((a, b) => {
		const byGrade = compareGrades(a.grade, b.grade);
		if (byGrade !== 0) return byGrade;
		const byCourse = a.course.localeCompare(b.course);
		if (byCourse !== 0) return byCourse;
		return a.resourceTitle.localeCompare(b.resourceTitle);
	});
}

export type CatalogRow = {
	courseId: string;
	grade: string;
	discipline: string;
	division: string;
	course: string;
	ap: string;
	owners: string;
	lastUpdated: string;
	status: string;
};

export function buildCatalogRows(courses: ReportCourse[]): CatalogRow[] {
	return [...courses]
		.sort((a, b) => {
			const byGrade = compareGrades(a.grade, b.grade);
			if (byGrade !== 0) return byGrade;
			return (a.course_name ?? '').localeCompare(b.course_name ?? '');
		})
		.map((course) => ({
			courseId: course.course_id,
			grade: course.grade ?? '',
			discipline: getCourseDiscipline(course)?.name ?? '',
			division: getCourseDivision(course)?.name ?? '',
			course: course.course_name ?? '',
			ap: course.is_ap ? 'Yes' : 'No',
			owners: getCourseOwners(course),
			lastUpdated: course.last_updated ? new Date(course.last_updated).toLocaleDateString() : '',
			status: getCourseStatus(course),
		}));
}

export type OutcomesRow = {
	courseId: string;
	outcomeId: string;
	grade: string;
	discipline: string;
	course: string;
	outcomeText: string;
};

export function buildOutcomesRows(courses: ReportCourse[]): OutcomesRow[] {
	const rows: OutcomesRow[] = [];

	for (const course of courses) {
		const discipline = getCourseDiscipline(course)?.name ?? '';
		const grade = course.grade ?? '';
		const outcomes = [...(course.courses_outcomes ?? [])].sort(
			(a, b) => (a.outcome?.outcome_order ?? 0) - (b.outcome?.outcome_order ?? 0)
		);

		for (const co of outcomes) {
			if (!co.outcome) continue;
			rows.push({
				courseId: course.course_id,
				outcomeId: co.outcome.outcome_id,
				grade,
				discipline,
				course: course.course_name ?? '',
				outcomeText: htmlToPlainText(co.outcome.outcome_text),
			});
		}
	}

	return rows.sort((a, b) => {
		const byGrade = compareGrades(a.grade, b.grade);
		if (byGrade !== 0) return byGrade;
		return a.course.localeCompare(b.course);
	});
}

export type ReadinessGapKey =
	| 'withGaps'
	| 'missingDescription'
	| 'noOutcomes'
	| 'noResources'
	| 'noUnits'
	| 'noOwner'
	| 'notOnMap'
	| 'draft';

export type ReadinessRow = {
	courseId: string;
	grade: string;
	discipline: string;
	course: string;
	missingDescription: boolean;
	noOutcomes: boolean;
	noResources: boolean;
	noUnits: boolean;
	noOwner: boolean;
	notOnMap: boolean;
	draft: boolean;
	gapCount: number;
};

export function buildReadinessRows(
	courses: ReportCourse[],
	unmappedCourseIds: Set<string>
): ReadinessRow[] {
	return courses
		.map((course) => {
			const missingDescription = !htmlToPlainText(course.course_description);
			const noOutcomes = (course.courses_outcomes ?? []).length === 0;
			const noResources = (course.courses_resources ?? []).length === 0;
			const noUnits = (course.courses_focuses ?? []).length === 0;
			const noOwner = (course.courses_users ?? []).length === 0;
			const notOnMap = unmappedCourseIds.has(course.course_id);
			const draft = Boolean(course.is_saved_draft);
			const gaps = [missingDescription, noOutcomes, noResources, noUnits, noOwner, notOnMap, draft];

			return {
				courseId: course.course_id,
				grade: course.grade ?? '',
				discipline: getCourseDiscipline(course)?.name ?? '',
				course: course.course_name ?? '',
				missingDescription,
				noOutcomes,
				noResources,
				noUnits,
				noOwner,
				notOnMap,
				draft,
				gapCount: gaps.filter(Boolean).length,
			};
		})
		.sort((a, b) => {
			if (a.gapCount !== b.gapCount) return b.gapCount - a.gapCount;
			const byGrade = compareGrades(a.grade, b.grade);
			if (byGrade !== 0) return byGrade;
			return a.course.localeCompare(b.course);
		});
}

export type ReadinessGapType = Exclude<ReadinessGapKey, 'withGaps'>;

/** Filter readiness rows by gaps-only and/or one-or-more gap types (OR). */
export function filterReadinessRows(
	rows: ReadinessRow[],
	filter: {
		gapsOnly?: boolean;
		gapTypes?: ReadinessGapType[];
	} = {}
): ReadinessRow[] {
	const gapTypes = filter.gapTypes ?? [];
	if (gapTypes.length > 0) {
		return rows.filter((row) => gapTypes.some((gap) => row[gap]));
	}
	if (filter.gapsOnly) {
		return rows.filter((row) => row.gapCount > 0);
	}
	return rows;
}

/** Fuse options for report tables — visible text only, light fuzzy / prefix-friendly. */
export const reportSearchFuseOptions: Fuse.IFuseOptions<{ searchText: string }> = {
	keys: ['searchText'],
	threshold: 0.2,
	ignoreLocation: true,
	minMatchCharLength: 2,
	findAllMatches: true,
	shouldSort: true,
};

/** Create a Fuse index over report rows (reuse across keystrokes). */
export function createReportSearchFuse<T extends { searchText: string }>(rows: T[]) {
	return new Fuse(rows, reportSearchFuseOptions as Fuse.IFuseOptions<T>);
}

/**
 * Fuzzy search report rows by visible searchText (Fuse.js).
 * Empty query returns all rows.
 */
export function filterRowsBySearchText<T extends { searchText: string }>(
	rows: T[],
	query: string,
	fuse?: Fuse<T>
): T[] {
	const trimmed = query.trim();
	if (!trimmed) return rows;

	const index = fuse ?? createReportSearchFuse(rows);
	return index.search(trimmed).map((result) => result.item);
}

export function summarizeReadiness(rows: ReadinessRow[]) {
	return {
		total: rows.length,
		withGaps: rows.filter((r) => r.gapCount > 0).length,
		missingDescription: rows.filter((r) => r.missingDescription).length,
		noOutcomes: rows.filter((r) => r.noOutcomes).length,
		noResources: rows.filter((r) => r.noResources).length,
		noUnits: rows.filter((r) => r.noUnits).length,
		noOwner: rows.filter((r) => r.noOwner).length,
		notOnMap: rows.filter((r) => r.notOnMap).length,
		draft: rows.filter((r) => r.draft).length,
	};
}

export function yesNo(value: boolean): string {
	return value ? 'Yes' : 'No';
}
